import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Field, InputGroup, Select, RemoveButton } from 'core/form';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';

import { Table } from 'core/components/table';
import CELL_TYPES from 'core/components/table/CELL_TYPES';
import CredentialDialog from 'app/views/settings/credentials/CredentialDialog';
import CloudExportAzureSpnCollection from 'app/stores/clouds/azure/CloudExportAzureSpnCollection';
import { Button, Box, Heading, ButtonGroupTab, ButtonGroupTabs, Card, Popover, Flex, Text } from 'core/components';

const TAB_CUSTOM_APP_REGISTRATION = 'app_registration';
const TAB_CUSTOM_KENTIK_ENTERPRISE_APPLICATION = 'kentik_enterprise_application';

@withRouter
@inject('$cloudExportWizard', '$credentials')
@observer
class AzureAuthenticationPanel extends Component {
  azureSpnCollection = new CloudExportAzureSpnCollection();

  state = {
    isLoading: false,
    popoverIsOpen: false,
    credentialModel: null,
    showAddCredentialDialog: false,
    isCredentialsOptionsLoading: true,
    isEditingPrevSpnCredentialSecret: false
  };

  async componentDidMount() {
    const { $credentials, $cloudExportWizard, form } = this.props;

    $credentials.groupedCollection.fetch().then(() => {
      this.setState({ isCredentialsOptionsLoading: false });
    });

    if (form) {
      $cloudExportWizard.setForm(form);
    }

    const selectedKentikAzureSpnId = $cloudExportWizard.getFieldValue('properties.kentik_azure_spn_id');

    if (selectedKentikAzureSpnId) {
      // load and set current exporter azure spn record
      this.azureSpnCollection.fetch({ force: true }).then(() => {
        this.handleSelectPreviousAzureSpnRecord(
          this.azureSpnCollection.find((model) => model.get('id') === selectedKentikAzureSpnId)
        );
      });
    }
  }

  handleTabChange = (tabId) => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.azure_api_access_method', tabId);
  };

  handleSelectPreviousAzureSpnRecord = (model) => {
    const { $cloudExportWizard } = this.props;

    if (!model) {
      return;
    }

    $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', model.get('id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_id', model.get('spn_app_id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_directory_id', model.get('spn_tenant_id'));
    $cloudExportWizard.setFieldValue(
      'properties.kentik_credential_secret_name',
      model.get('metadata.mn_credential_vault_name')
    );

    this.setState({ popoverIsOpen: false, isEditingPrevSpnCredentialSecret: false });
  };

  handleDeletePreviousAzureSpnRecord = async (model) => {
    if (!model) {
      return;
    }

    this.setState({ isLoading: true });

    await model.destroyModel();

    this.setState({ isLoading: false });
  };

  handleEditPreviousAzureSpnCredentialSecret = (model) => {
    const { $cloudExportWizard } = this.props;

    if (!model) {
      return;
    }

    // Keep the saved app registration selected, but allow editing only the secret name.
    $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', model.get('id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_id', model.get('spn_app_id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_directory_id', model.get('spn_tenant_id'));
    $cloudExportWizard.setFieldValue(
      'properties.kentik_credential_secret_name',
      model.get('metadata.mn_credential_vault_name')
    );

    this.setState({ popoverIsOpen: false, isEditingPrevSpnCredentialSecret: true });
  };

  handleCreateNewAzureSpnRecord = () => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', '');
    $cloudExportWizard.setFieldValue('properties.azure_application_id', '');
    $cloudExportWizard.setFieldValue('properties.azure_application_directory_id', '');
    $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', '');

    this.setState({ isEditingPrevSpnCredentialSecret: false });
  };

  handleSaveEditedCredentialSecret = async () => {
    const { $cloudExportWizard } = this.props;

    this.setState({ isLoading: true });

    const azureSpnModel = this.azureSpnCollection.find(
      (spnModel) => $cloudExportWizard.getFieldValue('properties.kentik_azure_spn_id') === spnModel.get('id')
    );

    if (!azureSpnModel) {
      console.error('Unable to find azure spn model to update credential secret');
      this.setState({ isEditingPrevSpnCredentialSecret: false, isLoading: false });
      return;
    }

    azureSpnModel.set(
      'mn_credential_vault_name',
      $cloudExportWizard.getFieldValue('properties.kentik_credential_secret_name')
    );

    try {
      await azureSpnModel.save();
    } catch (error) {
      console.error('Unable to update azure spn secret', error);
    }

    this.setState({ isEditingPrevSpnCredentialSecret: false, isLoading: false });
  };

  handleLoadPreviousAzureSpnRecords = async () => {
    this.setState((prevState) => ({ popoverIsOpen: !prevState.popoverIsOpen }));
    await this.azureSpnCollection.fetch({ force: true });
  };

  handleSaveNewAzureSpnRecord = async () => {
    const { $cloudExportWizard } = this.props;

    this.setState({ isLoading: true });

    const azureSpnModel = this.azureSpnCollection.forge({
      spn_app_id: $cloudExportWizard.getFieldValue('properties.azure_application_id'),
      spn_tenant_id: $cloudExportWizard.getFieldValue('properties.azure_application_directory_id'),
      mn_credential_vault_name: $cloudExportWizard.getFieldValue('properties.kentik_credential_secret_name')
    });

    try {
      await azureSpnModel.save();
      $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', azureSpnModel.get('id'));
    } catch (error) {
      console.error('Unable to save azure spn record', error);
    }

    this.setState({ isLoading: false });
  };

  get previousAzureSpnColumns() {
    return [
      {
        label: 'Application ID',
        name: 'spn_app_id',
        ellipsis: false
      },
      {
        label: 'Directory ID',
        name: 'spn_tenant_id',
        ellipsis: false
      },
      {
        label: 'Kentik Credential Secret',
        name: 'metadata',
        ellipsis: true,
        renderer: ({ value }) => (
          <Flex alignItems="center" justifyContent="space-between" gap={1} width="100%">
            <Text>{value?.mn_credential_vault_name}</Text>
          </Flex>
        )
      },
      {
        name: 'actions',
        width: 70,
        type: CELL_TYPES.ACTION,
        actions: [
          (model) => (
            <Button
              key="edit"
              icon="edit"
              ariaLabel="Edit"
              minimal
              small
              onClick={(e) => {
                e.stopPropagation();
                this.handleEditPreviousAzureSpnCredentialSecret(model);
              }}
            />
          ),
          (model) => (
            <RemoveButton
              icon="trash"
              minimal
              small
              showIcon
              hideText
              key="remove"
              entityName="label"
              ariaLabel="Remove"
              model={model}
              popoverProps={{
                confirmBodyContent: 'Remove Previous Azure App Registration',
                confirmText: 'Are you sure you want to delete this App Registration?'
              }}
              onRemove={() => this.handleDeletePreviousAzureSpnRecord(model)}
            />
          )
        ]
      }
    ];
  }

  handleCloseCredentialsDialog = (formValues) => {
    const { $cloudExportWizard } = this.props;
    this.setState({ showAddCredentialDialog: false });

    if (formValues) {
      const { name, tokens } = formValues;
      $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', `${name}.${tokens[0].key}`);
    }
  };

  handleClickAddCredentialDialog = () => {
    const { $credentials } = this.props;

    this.setState({
      showAddCredentialDialog: true,
      credentialModel: $credentials.groupedCollection.forge({ type: CREDENTIAL_TYPES.cloud })
    });
  };

  render() {
    const { $cloudExportWizard, $credentials } = this.props;
    const { isLoading, popoverIsOpen, showAddCredentialDialog, credentialModel, isCredentialsOptionsLoading } =
      this.state;

    const { isEditingPrevSpnCredentialSecret } = this.state;

    const isPrevSpnSelected = $cloudExportWizard.getFieldValue('properties.kentik_azure_spn_id');

    const allowSave =
      !isPrevSpnSelected &&
      $cloudExportWizard.getField('properties.azure_application_id')?.valid &&
      $cloudExportWizard.getField('properties.kentik_credential_secret_name')?.valid &&
      $cloudExportWizard.getField('properties.azure_application_directory_id')?.valid;

    const allowSecretSave =
      isEditingPrevSpnCredentialSecret &&
      $cloudExportWizard.getField('properties.kentik_credential_secret_name')?.valid;

    return (
      <Flex flex={1} flexDirection="column" gap="1">
        <ButtonGroupTabs
          omitTabsWrapper
          onChange={this.handleTabChange}
          selectedTabId={
            $cloudExportWizard.getFieldValue('properties.azure_api_access_method') ||
            TAB_CUSTOM_KENTIK_ENTERPRISE_APPLICATION
          }
        >
          <ButtonGroupTab
            id={TAB_CUSTOM_KENTIK_ENTERPRISE_APPLICATION}
            text="Kentik enterprise application"
            panel={<Text small>Kentik subscription id will be used to api requests.</Text>}
          />
          <ButtonGroupTab
            id={TAB_CUSTOM_APP_REGISTRATION}
            title="Custom app registration"
            panel={
              <Card bg="backgrounds.app">
                <Flex flexDirection="column" gap={1} p={2}>
                  <Flex justifyContent="space-between">
                    <Text as="div" fontWeight="bold" mb={1}>
                      Details
                    </Text>

                    <Flex gap={1}>
                      {isPrevSpnSelected && (
                        <Button small text="Create new" onClick={this.handleCreateNewAzureSpnRecord} />
                      )}
                      <Popover
                        usePortal
                        isOpen={popoverIsOpen}
                        position="right"
                        interactionKind={PopoverInteractionKind.CLICK}
                        minimal={false}
                        modifiers={{
                          flip: { enabled: false },
                          preventOverflow: { enabled: true, boundariesElement: 'window' }
                        }}
                        content={
                          <Box flex={1} p={2} minWidth={500}>
                            <Heading level={4}>Azure App Registrations</Heading>
                            <Flex flexDirection="column" border="thin">
                              <Table
                                collection={this.azureSpnCollection}
                                columns={this.previousAzureSpnColumns}
                                onRowClick={(model, e) => {
                                  e.stopPropagation();
                                  this.handleSelectPreviousAzureSpnRecord(model);
                                }}
                              />
                            </Flex>
                          </Box>
                        }
                        target={
                          <Button
                            small
                            icon="archive"
                            text="Saved App Registrations"
                            onClick={this.handleLoadPreviousAzureSpnRecords}
                          />
                        }
                      />
                    </Flex>
                  </Flex>

                  <Field name="properties.azure_application_id" width="100%" disabled={isPrevSpnSelected} large>
                    <InputGroup />
                  </Field>

                  <Field
                    name="properties.azure_application_directory_id"
                    width="100%"
                    disabled={isPrevSpnSelected}
                    large
                  >
                    <InputGroup />
                  </Field>

                  {!isPrevSpnSelected && (
                    <Flex justifyContent="space-between" gap={6}>
                      <Field
                        flex="1"
                        name="properties.kentik_credential_secret_name"
                        options={$credentials.getCredentialTokenOptionsByTypes([CREDENTIAL_TYPES.cloud])}
                        large
                      >
                        <Select loading={isCredentialsOptionsLoading} menuWidth="100%" />
                      </Field>
                      <Flex flexDirection="column" alignItems="flex-end">
                        <Button
                          onClick={this.handleClickAddCredentialDialog}
                          text="Create New Secret"
                          mt="auto"
                          mb={1}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {isPrevSpnSelected && isEditingPrevSpnCredentialSecret && (
                    <Flex justifyContent="space-between" gap={6} alignItems="center">
                      <Field
                        flex="1"
                        maxWidth="150px"
                        name="properties.kentik_credential_secret_name"
                        options={$credentials.getCredentialTokenOptionsByTypes([CREDENTIAL_TYPES.cloud])}
                        large
                      >
                        <Select loading={isCredentialsOptionsLoading} menuWidth="100%" />
                      </Field>
                      <Flex flexDirection="column" alignItems="flex-end">
                        <Button
                          onClick={this.handleSaveEditedCredentialSecret}
                          text="Update Credential"
                          loading={isLoading}
                          disabled={!allowSecretSave}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {credentialModel && (
                    <CredentialDialog
                      model={credentialModel}
                      isOpen={showAddCredentialDialog}
                      onClose={this.handleCloseCredentialsDialog}
                      hideTypeSelector
                    />
                  )}

                  {!isPrevSpnSelected && (
                    <Flex justifyContent="flex-end">
                      <Button
                        disabled={!allowSave}
                        loading={isLoading}
                        onClick={this.handleSaveNewAzureSpnRecord}
                        text="Save App Registration"
                      />
                    </Flex>
                  )}
                </Flex>
              </Card>
            }
          />
        </ButtonGroupTabs>
      </Flex>
    );
  }
}

export default AzureAuthenticationPanel;
